import { EmitterBase } from "../webrtc/event_emitter";
import { StreamWebRTC } from "../webrtc/stream_webrtc";
import { generateString } from "../common/util";
import { AppError } from "../common/app_error";
import { Stats } from "../common/stats";
export class ControllerBase extends EmitterBase {
    constructor(options, rtcClsType) {
        super();
        this.connecting = false;
        this.closed = false;
        //
        this.debugging = false;
        this.orgId = options.orgId;
        this.nvrId = options.nvrId;
        this.connectId = generateString(31);
        this.rtcToken = options.rtcToken;
        this.signal = options.signal;
        this.turn = options.turn;
        let iceServer = this.turn;
        //@ts-ignore
        if (options.debugging || window.WebRTCDebug === true) {
            this.debugging = true;
        }
        options.featureSettings = options.featureSettings || {};
        //@ts-ignore
        if (window.WebRTCFrameStats === true) {
            options.featureSettings.frameStats = true;
        }
        this.stats = new Stats('webrtc_controller', this.debugging);
        //
        this.webRTC = new rtcClsType({ iceServer }, {
            connId: this.connectId,
            debugging: options.debugging,
            workerDebug: options.workerDebug,
            featureSettings: options.featureSettings,
            errorHandler: options.errorHandler,
            videoCodec: options.videoCodec,
        });
        this.webRTC.addEventListener(StreamWebRTC.EVENT_CREATE_OFFER, (the, offer, traceHeader) => {
            this.onSendOffer(offer, traceHeader);
        });
        this.webRTC.addEventListener(StreamWebRTC.EVENT_RECONNECT, (the, offer, func) => {
            this.reconnect(offer, func);
        });
        this.webRTC.addEventListener(StreamWebRTC.EVENT_ICE_CANDIDATES, (the) => {
            this.sendIceCandidates();
        });
        this.webRTC.addEventListener(StreamWebRTC.EVENT_CLOSE, (the) => {
            this.emitClose(ControllerBase.CLOSE_WEBRTC);
        });
        this.webRTC.addEventListener(StreamWebRTC.EVENT_ERROR, (e) => {
            this.emitError(e);
        });
        this.webRTC.addEventListener(StreamWebRTC.EVENT_CONNECTED, (the) => {
            this.connecting = false;
            this.emitEvent(ControllerBase.EVENT_READY);
        });
    }
    onSendOffer(offer, traceHeader) {
        this.stats.start({ key: ControllerBase.STATS_OFFER });
        this.sendOffer(offer, traceHeader);
    }
    reconnect(offer, func) {
        this.signal.reconnect({ orgId: this.orgId, connectId: this.connectId, sdp: offer.sdp, token: this.rtcToken, source: this, cb: func });
    }
    addEventListenerToWebRtc(event, callback) {
        return this.webRTC.addEventListener(event, callback);
    }
    start() {
        let starting = this.webRTC.start();
        if (starting) {
            this.connecting = true;
            setTimeout(() => {
                if (this.connecting) {
                    this.emitClose(ControllerBase.CLOSE_TIMEOUT);
                }
            }, ControllerBase.CONNECT_TIMEOUT);
        }
        return starting;
    }
    onAnswer(data) {
        this.stats.end({ key: ControllerBase.STATS_OFFER });
        this.emitEvent(ControllerBase.EVENT_ANSWER, data);
        this.doOnAnswer(data).catch(console.error);
    }
    onReconnectAnswer(reply) {
        this.webRTC.putAnswer(reply.answer);
    }
    async doOnAnswer(reply) {
        await this.webRTC.putAnswer(reply.answer);
    }
    onIceCandidates(candidates) {
        this.webRTC.addIceCandidates(candidates).catch(console.error);
    }
    triggerReconnect() {
        this.webRTC.reconnect();
    }
    sendIceCandidates() {
        this.doSendIceCandidates();
    }
    doSendIceCandidates() {
        let iceCandidates;
        while (true) {
            iceCandidates = this.webRTC.shiftIceCandidates(ControllerBase.SEND_ICE_LIMIT);
            if (!iceCandidates.length)
                break;
            this.signal.sendIceCandidates(this, { orgId: this.orgId, connectId: this.connectId, candidates: iceCandidates, rtcToken: this.rtcToken });
        }
    }
    emitError(error) {
        if (!(error instanceof AppError)) {
            error = AppError.fromError(error);
        }
        this.emitEvent(ControllerBase.EVENT_ERROR, error);
    }
    onError(error) {
        try {
            console.error('onError: error=%s, desc=%s, connectId=%s', error.message, error.stack, this.connectId);
            this.emitError(error);
        }
        catch (e) {
            console.error('onError: error=%s, desc=%s, connectId=%s', error.message, error.stack, this.connectId);
        }
        if (['edge.bandwidth_limit', 'edge.webrtc.conn_limit', 'edge.speed_testing'].includes(error.name)) {
            this.emitClose(ControllerBase.CLOSE_BUSY);
        }
        else {
            this.emitClose(ControllerBase.CLOSE_SIGNAL);
        }
    }
    doClose() {
        this.webRTC.close();
        this.signal.detach(this);
    }
    emitClose(reason) {
        if (this.closed)
            return;
        this.closed = true;
        this.doClose();
        this.emitEvent(ControllerBase.EVENT_CLOSE, reason);
    }
    close() {
        this.emitClose(ControllerBase.CLOSE_NORMAL);
    }
    isWebRTCConnected() {
        return this.webRTC.isConnected();
    }
    getDesc() {
        return JSON.stringify(this.doGetDesc());
    }
}
ControllerBase.EVENT_CLOSE = 'close';
ControllerBase.EVENT_ANSWER = 'answer';
ControllerBase.EVENT_ERROR = 'error';
ControllerBase.EVENT_READY = 'ready';
///
ControllerBase.CLOSE_WEBRTC = 'webrtc';
ControllerBase.CLOSE_SIGNAL = 'signal';
ControllerBase.CLOSE_BUSY = 'busy';
ControllerBase.CLOSE_NORMAL = 'normal';
ControllerBase.CLOSE_TIMEOUT = 'timeout';
//
ControllerBase.SEND_ICE_LIMIT = 63;
// ms
ControllerBase.CONNECT_TIMEOUT = 15000;
// stats
ControllerBase.STATS_OFFER = 'offer';
ControllerBase.STATS_RECONNECT = 'reconnect';
